import { z } from 'zod';
import {
  EQUIPMENT_ATTRIBUTE_LABELS,
  EQUIPMENT_ATTRIBUTE_UNITS,
  EQUIPMENT_GUARANTEED_ATTRIBUTES,
  EQUIPMENT_MAX_LEVEL,
  type EquipmentAttribute,
  equipmentAttributeSchema,
  equipmentComponentSchema,
  equipmentRaritySchema,
} from './catalog';
import { effectiveAttributeValue } from './levels';
import { type EquipmentRoll, rollProblems } from './roll';

/**
 * The persisted roll, and the shape the client is allowed to see.
 *
 * Parsing is FAIL-LOUD in both directions. A roll comes out of the database and
 * is validated before anything reads it, because a row that was hand-edited,
 * written by an older build or corrupted must not become an attribute nobody
 * can explain. `rollProblems` is what does the checking, so the invariants live
 * with the rules that create them rather than being restated here.
 */

export const rolledAttributeSchema = z
  .object({
    attribute: equipmentAttributeSchema,
    /** The IMMUTABLE level-1 value, in the attribute's own integer unit. */
    baseValue: z.number().int().min(0),
  })
  .strict();

export const equipmentRollSchema = z
  .object({
    component: equipmentComponentSchema,
    rarity: equipmentRaritySchema,
    rollVersion: z.number().int().min(1),
    attributes: z.array(rolledAttributeSchema).min(1).max(9),
  })
  .strict();

export class InvalidEquipmentRollError extends Error {
  constructor(detail: string) {
    // Names the problem, never echoes a player's numbers into a log.
    super(`invalid equipment roll: ${detail}`);
    this.name = 'InvalidEquipmentRollError';
  }
}

/** Validates a persisted roll's SHAPE and then its RULES. Throws on either. */
export function parseEquipmentRoll(value: unknown): EquipmentRoll {
  const parsed = equipmentRollSchema.parse(value);
  const problems = rollProblems(parsed);
  if (problems.length > 0) {
    throw new InvalidEquipmentRollError(problems.join('; '));
  }
  return parsed;
}

/* -------------------------------------------------------------------------- */
/*  Display                                                                   */
/* -------------------------------------------------------------------------- */

/** How many decimal places each unit is written with, and its suffix. */
const UNIT_FORMAT: Readonly<
  Record<string, { readonly decimals: number; readonly suffix: string }>
> = Object.freeze({
  milliDamage: { decimals: 2, suffix: '' },
  bps: { decimals: 2, suffix: '%' },
  microAttacksPerSecond: { decimals: 3, suffix: '/s' },
  milliHealth: { decimals: 0, suffix: '' },
  milliGold: { decimals: 3, suffix: '' },
});

/** The divisor that turns a stored sub-unit into the unit players read. */
const UNIT_DIVISOR: Readonly<Record<string, bigint>> = Object.freeze({
  milliDamage: 1_000n,
  bps: 100n,
  microAttacksPerSecond: 1_000_000n,
  milliHealth: 1_000n,
  milliGold: 1_000n,
});

/**
 * A stored integer, written the way the panel shows it.
 *
 * Formatted from the exact `bigint` with integer arithmetic - never by dividing
 * into a float first. A comma is the decimal separator, because the game speaks
 * Brazilian Portuguese.
 */
export function formatAttributeValue(attribute: EquipmentAttribute, value: bigint): string {
  const unit = EQUIPMENT_ATTRIBUTE_UNITS[attribute];
  const divisor = UNIT_DIVISOR[unit] ?? 1n;
  const format = UNIT_FORMAT[unit] ?? { decimals: 0, suffix: '' };
  const whole = value / divisor;
  if (format.decimals === 0) {
    return `+${whole.toString(10)}${format.suffix}`;
  }
  const scale = 10n ** BigInt(format.decimals);
  const fraction = ((value % divisor) * scale) / divisor;
  const text = fraction.toString(10).padStart(format.decimals, '0').replace(/0+$/, '');
  return text.length === 0
    ? `+${whole.toString(10)}${format.suffix}`
    : `+${whole.toString(10)},${text}${format.suffix}`;
}

/** One attribute of one instance, as the client sees it. */
export const equipmentAttributeViewSchema = z.object({
  attribute: equipmentAttributeSchema,
  label: z.string(),
  /** Exact integers as decimal strings; a JSON number would round eventually. */
  baseValue: z.string(),
  effectiveValue: z.string(),
  /** The same two numbers, already written the way the panel shows them. */
  baseText: z.string(),
  effectiveText: z.string(),
  /** True for the attribute the component's identity guaranteed. */
  guaranteed: z.boolean(),
});
export type EquipmentAttributeView = z.infer<typeof equipmentAttributeViewSchema>;

/** Every attribute of an instance at a level, base and effective side by side. */
export function attributeViews(
  roll: EquipmentRoll,
  level: number,
): readonly EquipmentAttributeView[] {
  const guaranteedGroup = EQUIPMENT_GUARANTEED_ATTRIBUTES[roll.component];
  return roll.attributes.map((entry, index) => {
    const base = BigInt(entry.baseValue);
    const effective = effectiveAttributeValue(base, level);
    return {
      attribute: entry.attribute,
      label: EQUIPMENT_ATTRIBUTE_LABELS[entry.attribute],
      baseValue: base.toString(10),
      effectiveValue: effective.toString(10),
      baseText: formatAttributeValue(entry.attribute, base),
      effectiveText: formatAttributeValue(entry.attribute, effective),
      // Index 0 IS the guaranteed one by construction; the group check is a
      // belt-and-braces so a corrupted order cannot mislabel it.
      guaranteed: index === 0 && guaranteedGroup.includes(entry.attribute),
    };
  });
}

/**
 * One owned component, as the client is allowed to see it.
 *
 * Carries everything a decision needs - component, rarity, level, Power, every
 * base and effective value - and nothing else. No owner id, no reservation id,
 * no correlation id, no roll version secrets, nothing about another account.
 */
export const equipmentInstanceSchema = z.object({
  itemId: z.uuid(),
  definitionId: z.string().min(1),
  component: equipmentComponentSchema,
  componentLabel: z.string(),
  rarity: equipmentRaritySchema,
  level: z.number().int().min(1).max(EQUIPMENT_MAX_LEVEL),
  power: z.number().int().min(1),
  powerVersion: z.number().int().min(1),
  attributes: z.array(equipmentAttributeViewSchema),
  equipped: z.boolean(),
  /** The slot it occupies, or null when it is merely owned. */
  slot: z.string().nullable(),
  locked: z.boolean(),
  lockedPurpose: z.string().nullable(),
  soulbound: z.boolean(),
  availableForTrade: z.boolean(),
  unavailableReason: z.string().nullable(),
  assetKey: z.string().nullable(),
});
export type EquipmentInstanceWire = z.infer<typeof equipmentInstanceSchema>;

/** The five slots, always all five, empty ones included. */
export const equipmentSlotViewSchema = z.object({
  slot: z.string(),
  component: equipmentComponentSchema,
  label: z.string(),
  item: equipmentInstanceSchema.nullable(),
});
export type EquipmentSlotView = z.infer<typeof equipmentSlotViewSchema>;
